import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { API } from "../context/AuthContext";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(r.data.payment_status)) { setState("error"); return; }
      } catch {
        setState("error");
        return;
      }
      if (attempts.current >= 8) { setState("pending"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="bg-[#FFF8E7] min-h-[70vh] flex items-center justify-center px-4" data-testid="page-payment-success">
      <div className="max-w-lg w-full bg-white border-2 border-[#0A0A0A] pop-shadow p-10 text-center">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="w-12 h-12 mx-auto text-[#1A5DFF] animate-spin" />
            <h1 className="font-heading font-black text-3xl text-[#0A0A0A] mt-6">Confirming your payment…</h1>
            <p className="text-[#2C2C2C] mt-3">Hold tight — this usually takes a few seconds.</p>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="payment-confirmed">
            <span className="inline-flex w-16 h-16 bg-[#FF2E63] text-[#FFF8E7] items-center justify-center border-2 border-[#0A0A0A]">
              <CheckCircle2 className="w-8 h-8" />
            </span>
            <h1 className="font-heading font-black text-3xl text-[#0A0A0A] mt-6">Thank you!</h1>
            <p className="text-[#2C2C2C] mt-3 leading-relaxed">
              Your payment went through. Every pound goes to platforming neurodivergent filmmakers —
              honoraria, fee waivers and keeping the programme free to watch.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/films" data-testid="success-films-link" className="bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] border-2 border-[#0A0A0A] px-6 py-3 text-sm font-medium">Watch the programme</Link>
              <Link to="/network" data-testid="success-network-link" className="bg-[#FFD93D] text-[#0A0A0A] hover:bg-[#FFCB05] border-2 border-[#0A0A0A] px-6 py-3 text-sm font-medium">Open the network</Link>
            </div>
          </div>
        )}
        {state === "pending" && (
          <div data-testid="payment-pending">
            <AlertTriangle className="w-12 h-12 mx-auto text-[#FFD93D]" />
            <h1 className="font-heading font-black text-3xl text-[#0A0A0A] mt-6">Payment is processing</h1>
            <p className="text-[#2C2C2C] mt-3">We haven&apos;t received confirmation yet. If you completed checkout, it will land shortly — check your email for a Stripe receipt.</p>
            <Link to="/membership" data-testid="pending-back-link" className="inline-block mt-8 underline underline-offset-4 text-[#FF2E63] font-bold">Back to Support us</Link>
          </div>
        )}
        {state === "error" && (
          <div data-testid="payment-error">
            <AlertTriangle className="w-12 h-12 mx-auto text-[#FF2E63]" />
            <h1 className="font-heading font-black text-3xl text-[#0A0A0A] mt-6">Something went wrong</h1>
            <p className="text-[#2C2C2C] mt-3">We couldn&apos;t confirm this payment. If money left your account, email <a href="mailto:create@divergentcinema.co.uk" className="underline">create@divergentcinema.co.uk</a> and we&apos;ll sort it.</p>
            <Link to="/membership" data-testid="error-back-link" className="inline-block mt-8 underline underline-offset-4 text-[#FF2E63] font-bold">Back to Support us</Link>
          </div>
        )}
      </div>
    </div>
  );
}
